import React, { useEffect, useMemo, useRef, useState } from "react"
import { Tag, Descriptions } from "antd"
import Strings from "utils/strings"
import { getStatusColor, getStatusLabel } from "utils/status"
import { getVehicleTypeLabel } from "utils/appIcons"
import { GoogleMaps } from "components"
import { ALL_CONTRACTS_VALUE } from "hooks"
import { Route, RouteType } from "../types"
import { getRouteColor } from "../routeColors"
import { spreadOverlappingMarkers } from "../mapMarkerUtils"
import ChangeStatusButton from "./ChangeStatusButton"
import {
	useExternalDeliveryAssign,
	ExternalDeliverySelect,
	ExternalDeliveryModal,
	isRouteEligibleForExternalDelivery,
} from "./ExternalDeliveryAssign"
import { useSelector } from "react-redux"

// Fixed-order categorical palette (validated for adjacent CVD contrast) — used
// for routeType, which only ever has 3 possible values.
const ROUTE_TYPE_COLORS: Record<RouteType, string> = {
	van: "#2a78d6", // blue
	bike: "#1baf7a", // aqua
	fr: "#eda100", // yellow
}

interface MultiRoutePanelProps {
	selectedRoutes: Route[]
	totalRoutes: number
	onSelectAll: () => void
	setRoutes: React.Dispatch<React.SetStateAction<Route[]>>
	onClose: () => void
}

const MultiRoutePanel: React.FC<MultiRoutePanelProps> = ({
	selectedRoutes,
	totalRoutes,
	onSelectAll,
	setRoutes,
	onClose,
}) => {
	// Redux variables - memoize to prevent unnecessary re-renders
	const rawFilters = useSelector((state: any) => state.filters) || {}
	const { contractDetails, isAllContracts } = useMemo(
		() => ({
			contractDetails: rawFilters.contractDetails,
			isAllContracts: rawFilters.contract === ALL_CONTRACTS_VALUE,
		}),
		[rawFilters.contractDetails, rawFilters.contract]
	)

	const allSelected = selectedRoutes.length === totalRoutes

	const allSameStatus = useMemo(() => {
		if (selectedRoutes.length === 0) return false
		const firstStatus = selectedRoutes[0].status
		return selectedRoutes.every((route) => route.status === firstStatus)
	}, [selectedRoutes])

	const uniqueStatuses = useMemo(() => {
		const statusMap = new Map()
		selectedRoutes.forEach((route) => {
			if (route.status) {
				statusMap.set(route.status, (statusMap.get(route.status) || 0) + 1)
			}
		})
		return Array.from(statusMap.entries()).map(([status, count]) => ({
			status,
			count,
		}))
	}, [selectedRoutes])

	const dateDisplay = useMemo(() => {
		const dateSet = new Set<string>()
		selectedRoutes.forEach((route) => {
			if (route.date) {
				const formattedDate = new Date(route.date).toLocaleDateString()
				dateSet.add(formattedDate)
			}
		})
		const uniqueDates = Array.from(dateSet)
		if (uniqueDates.length === 0) return "-"
		if (uniqueDates.length === 1) return uniqueDates[0]
		return Strings.routes.multipleDates
	}, [selectedRoutes])

	// All selected routes must be dispatching, today, bike, and unassigned.
	const externalDeliveryEligible = useMemo(() => {
		if (selectedRoutes.length === 0) return false
		return selectedRoutes.every((route) =>
			isRouteEligibleForExternalDelivery(route)
		)
	}, [selectedRoutes])

	const totalDeliveriesCount = useMemo(
		() =>
			selectedRoutes.reduce(
				(sum, route) =>
					sum +
					(route.addresses || [])
						.filter(
							(addr) =>
								!addr.isDepot &&
								addr.order?.orderId &&
								addr.stopType?.toUpperCase() === "DELIVERY"
						)
						.reduce(
							(count, addr) =>
								count + 1 + (addr.order?.bundledOrders?.length || 0),
							0
						),
				0
			),
		[selectedRoutes]
	)

	const routeIds = useMemo(
		() => selectedRoutes.map((r) => r._id),
		[selectedRoutes]
	)

	const [mapColorMode, setMapColorMode] = useState<
		"route" | "routeType" | "contract"
	>("route")
	// "By contract" only makes sense with "All contracts" selected upstream —
	// fall back if the filter changes out from under this panel.
	useEffect(() => {
		if (!isAllContracts && mapColorMode === "contract") setMapColorMode("route")
	}, [isAllContracts, mapColorMode])

	// Routes toggled off by clicking their legend entry — hidden from the map
	// but kept in the legend (greyed out) so they can be toggled back on.
	const [hiddenRouteIds, setHiddenRouteIds] = useState<Set<string>>(new Set())
	// Routes highlighted by hovering their legend entry — everyone else dims.
	const [hoveredRouteIds, setHoveredRouteIds] = useState<string[] | null>(null)
	const [depotHidden, setDepotHidden] = useState(false)
	// Tracked live so the overlap spread (screen-pixel based) stays correct
	// as the user zooms in/out.
	const [mapZoom, setMapZoom] = useState(12)

	const toggleLegendGroup = (ids: string[]) => {
		setHiddenRouteIds((prev) => {
			const next = new Set(prev)
			const allHidden = ids.every((id) => next.has(id))
			ids.forEach((id) => (allHidden ? next.delete(id) : next.add(id)))
			return next
		})
	}

	// Color index per route/contract, assigned the first time each is seen and
	// never reassigned afterwards — otherwise adding or removing a route shifts
	// everyone else's position in the array and repaints colors that are
	// already on the map, which reads as routes randomly swapping colors.
	const routeColorIndexRef = useRef<Map<string, number>>(new Map())
	const contractColorIndexRef = useRef<Map<string, number>>(new Map())
	selectedRoutes.forEach((route) => {
		if (!routeColorIndexRef.current.has(route._id)) {
			routeColorIndexRef.current.set(route._id, routeColorIndexRef.current.size)
		}
		const contractId = route.contract || ""
		if (!contractColorIndexRef.current.has(contractId)) {
			contractColorIndexRef.current.set(
				contractId,
				contractColorIndexRef.current.size
			)
		}
	})

	// Stable color per contract, keyed off the never-reassigned index above.
	const contractColorMap = useMemo(() => {
		const map = new Map<string, string>()
		selectedRoutes.forEach((route) => {
			const contractId = route.contract || ""
			if (!map.has(contractId)) {
				map.set(
					contractId,
					getRouteColor(contractColorIndexRef.current.get(contractId) ?? 0)
				)
			}
		})
		return map
	}, [selectedRoutes])

	const hasMappableData = useMemo(
		() =>
			selectedRoutes.some((route) =>
				(route.addresses || []).some(
					(addr) => addr.location?.lat != null && addr.location?.lng != null
				)
			),
		[selectedRoutes]
	)

	const routeColor = (route: Route) => {
		if (mapColorMode === "routeType")
			return ROUTE_TYPE_COLORS[route.routeType] ?? "#8c8c8c"
		if (mapColorMode === "contract")
			return contractColorMap.get(route.contract || "") ?? "#8c8c8c"
		return getRouteColor(routeColorIndexRef.current.get(route._id) ?? 0)
	}

	const mapMarkers = useMemo(
		() =>
			selectedRoutes.flatMap((route) => {
				if (hiddenRouteIds.has(route._id)) return []
				const color = routeColor(route)
				// Spotlight effect: hovering a legend entry dims every other route.
				const isDimmed =
					hoveredRouteIds != null && !hoveredRouteIds.includes(route._id)

				return (route.addresses || [])
					.filter(
						(addr) =>
							!addr.isDepot &&
							addr.location?.lat != null &&
							addr.location?.lng != null
					)
					.map((addr, stopIndex) => ({
						lat: addr.location.lat,
						lng: addr.location.lng,
						label: {
							text: String(stopIndex + 1),
							color: "white",
							fontSize: "10px",
							fontWeight: "bold",
						},
						icon: {
							path: 0, // google.maps.SymbolPath.CIRCLE
							fillColor: color,
							fillOpacity: isDimmed ? 0.25 : 1,
							scale: isDimmed ? 7 : 9,
							strokeColor: "white",
							strokeWeight: 2,
						},
						overlay: (
							<div style={{ fontSize: 12, fontWeight: 600 }}>
								{route.name} · {Strings.routes.stopNumber} {stopIndex + 1}
							</div>
						),
					}))
			}),
		[
			selectedRoutes,
			mapColorMode,
			hiddenRouteIds,
			hoveredRouteIds,
			contractColorMap,
		]
	)

	// Depots are shared by multiple routes, so they get one marker per unique
	// location, colored like the (first) route that uses it — a diamond shape
	// keeps it visually distinct from the circular stop markers regardless of
	// which color it ends up with.
	const depotMarkers = useMemo(() => {
		const seen = new Set<string>()
		const markers: any[] = []
		selectedRoutes.forEach((route) => {
			if (hiddenRouteIds.has(route._id)) return
			const depotColor = routeColor(route)

			;(route.addresses || []).forEach((addr) => {
				if (
					!addr.isDepot ||
					addr.location?.lat == null ||
					addr.location?.lng == null
				)
					return
				const key = `${addr.location.lat.toFixed(5)},${addr.location.lng.toFixed(5)}`
				if (seen.has(key)) return
				seen.add(key)
				markers.push({
					lat: addr.location.lat,
					lng: addr.location.lng,
					label: {
						text: "D",
						color: "white",
						fontSize: "11px",
						fontWeight: "bold",
					},
					icon: {
						path: "M 0,-10 L 10,0 L 0,10 L -10,0 Z", // diamond
						fillColor: depotColor,
						fillOpacity: 1,
						scale: 1,
						strokeColor: "white",
						strokeWeight: 2,
					},
				})
			})
		})
		return markers
	}, [selectedRoutes, mapColorMode, contractColorMap, hiddenRouteIds])

	// Spread as the very last step, over the combined set — a bike stop can
	// sit exactly on a van's depot (resupplying from it), not just on another
	// stop, so stop-vs-stop, stop-vs-depot and depot-vs-depot all need it.
	const allMapMarkers = useMemo(
		() =>
			spreadOverlappingMarkers(
				[...mapMarkers, ...(depotHidden ? [] : depotMarkers)],
				mapZoom
			),
		[mapMarkers, depotMarkers, depotHidden, mapZoom]
	)

	const mapBoundingBox = useMemo(() => {
		if (allMapMarkers.length === 0) return undefined
		const lats = allMapMarkers.map((m) => m.lat)
		const lngs = allMapMarkers.map((m) => m.lng)
		const pad = 0.003
		return {
			south: Math.min(...lats) - pad,
			north: Math.max(...lats) + pad,
			west: Math.min(...lngs) - pad,
			east: Math.max(...lngs) + pad,
		}
	}, [allMapMarkers])

	// Stop count (excluding depot) across a group of routes — shown in the
	// legend chip so it carries more than just an identity/color.
	const countStops = (routes: Route[]) =>
		routes.reduce(
			(sum, route) =>
				sum +
				(route.addresses || []).filter(
					(addr) =>
						!addr.isDepot &&
						addr.location?.lat != null &&
						addr.location?.lng != null
				).length,
			0
		)

	const mapLegend = useMemo(() => {
		if (mapColorMode === "routeType") {
			const types = Array.from(new Set(selectedRoutes.map((r) => r.routeType)))
			return types.map((type) => {
				const routesForType = selectedRoutes.filter((r) => r.routeType === type)
				return {
					key: type,
					label: getVehicleTypeLabel(type),
					color: ROUTE_TYPE_COLORS[type] ?? "#8c8c8c",
					routeIds: routesForType.map((r) => r._id),
					count: countStops(routesForType),
				}
			})
		}
		if (mapColorMode === "contract") {
			const contractIds = Array.from(
				new Set(selectedRoutes.map((r) => r.contract || ""))
			)
			return contractIds.map((contractId) => {
				const routesForContract = selectedRoutes.filter(
					(r) => (r.contract || "") === contractId
				)
				return {
					key: contractId || "unknown",
					label: routesForContract[0]?.contractName || "—",
					color: contractColorMap.get(contractId) ?? "#8c8c8c",
					routeIds: routesForContract.map((r) => r._id),
					count: countStops(routesForContract),
				}
			})
		}
		return selectedRoutes.map((route) => ({
			key: route._id,
			label: route.name || route.systemName,
			color: routeColor(route),
			routeIds: [route._id],
			count: countStops([route]),
		}))
	}, [selectedRoutes, mapColorMode, contractColorMap])

	const {
		selectedPartner,
		setSelectedPartner,
		showModal,
		setShowModal,
		submitting,
		handleSubmit,
		failedRoutes,
		clearFailedRoutes,
	} = useExternalDeliveryAssign({ routeIds, setRoutes })

	return (
		<div className="RoutesScreen__multi-select">
			<div className="RoutesScreen__multi-select-header">
				<h3>
					{Strings.formatString(
						Strings.routes.routesSelected,
						selectedRoutes.length
					)}
				</h3>
				<div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
					{!allSelected && (
						<button
							onClick={onSelectAll}
							className="RoutesScreen__multi-select-select-all"
						>
							{Strings.routes.selectAll}
						</button>
					)}
					<button onClick={onClose}>×</button>
				</div>
			</div>
			<div className="RoutesScreen__multi-select-content">
				{allSameStatus && (
					<ChangeStatusButton routes={selectedRoutes} setRoutes={setRoutes} />
				)}

				<div className="RouteDetailPanel__section">
					<Descriptions column={1} bordered size="small">
						<Descriptions.Item label={Strings.routes.status}>
							<div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
								{uniqueStatuses.map(({ status, count }) => (
									<Tag key={status} color={getStatusColor(status)}>
										{getStatusLabel(status)} ({count})
									</Tag>
								))}
							</div>
						</Descriptions.Item>
						<Descriptions.Item label={Strings.fields.date}>
							{dateDisplay}
						</Descriptions.Item>
						{contractDetails?.allowExternalDelivery && (
							<Descriptions.Item label={Strings.routes.externalDelivery}>
								<ExternalDeliverySelect
									value={selectedPartner}
									onChange={(val) => {
										setSelectedPartner(val)
										if (val) setShowModal(true)
									}}
									disabled={!externalDeliveryEligible}
									placeholder={
										externalDeliveryEligible
											? Strings.routes.selectExternalProvider
											: Strings.routes.externalDeliveryMultiNotEligible
									}
								/>
							</Descriptions.Item>
						)}
					</Descriptions>
				</div>

				{hasMappableData && (
					<div className="RoutesScreen__multi-select-map-card">
						<div className="RoutesScreen__multi-select-map-card-header">
							<h4>{Strings.routes.routesMap}</h4>
							<div className="RoutesScreen__multi-select-map-color-toggle">
								<button
									type="button"
									className={mapColorMode === "route" ? "active" : ""}
									onClick={() => setMapColorMode("route")}
								>
									{Strings.routes.colorByRoute}
								</button>
								<button
									type="button"
									className={mapColorMode === "routeType" ? "active" : ""}
									onClick={() => setMapColorMode("routeType")}
								>
									{Strings.routes.colorByRouteType}
								</button>
								{isAllContracts && (
									<button
										type="button"
										className={mapColorMode === "contract" ? "active" : ""}
										onClick={() => setMapColorMode("contract")}
									>
										{Strings.routes.colorByContract}
									</button>
								)}
							</div>
						</div>
						<GoogleMaps
							boundingBox={mapBoundingBox}
							markers={allMapMarkers}
							mapContainerStyle={{
								width: "100%",
								height: 500,
								borderRadius: 8,
							}}
							options={{
								gestureHandling: "cooperative",
								clickableIcons: false,
							}}
							onMapLoad={(map) => {
								const updateZoom = () => setMapZoom(map.getZoom() ?? 12)
								// fitBounds() recalculates zoom asynchronously, so reading
								// getZoom() right away can still reflect the pre-fit zoom —
								// "idle" fires once the map has settled after the fit.
								google.maps.event.addListenerOnce(map, "idle", updateZoom)
								map.addListener("zoom_changed", updateZoom)
							}}
						/>
						<div className="RoutesScreen__multi-select-map-legend">
							{depotMarkers.length > 0 && (
								<button
									type="button"
									className={`RoutesScreen__multi-select-map-legend-item${depotHidden ? " is-hidden" : ""}`}
									title={Strings.routes.depot}
									onClick={() => setDepotHidden((prev) => !prev)}
								>
									<span
										style={{
											backgroundColor: depotHidden ? "#d9d9d9" : "#434343",
										}}
										className="RoutesScreen__multi-select-map-legend-dot RoutesScreen__multi-select-map-legend-dot--diamond"
									/>
									<span className="RoutesScreen__multi-select-map-legend-label">
										{Strings.routes.depot}
									</span>
								</button>
							)}
							{mapLegend.map((item) => {
								const isHidden = item.routeIds.every((id) =>
									hiddenRouteIds.has(id)
								)
								return (
									<button
										type="button"
										key={item.key}
										className={`RoutesScreen__multi-select-map-legend-item${isHidden ? " is-hidden" : ""}`}
										title={item.label}
										onClick={() => toggleLegendGroup(item.routeIds)}
										onMouseEnter={() => setHoveredRouteIds(item.routeIds)}
										onMouseLeave={() => setHoveredRouteIds(null)}
									>
										<span
											style={{
												backgroundColor: isHidden ? "#d9d9d9" : item.color,
											}}
											className="RoutesScreen__multi-select-map-legend-dot"
										/>
										<span className="RoutesScreen__multi-select-map-legend-label">
											{item.label}
										</span>
										<span className="RoutesScreen__multi-select-map-legend-count">
											{item.count}
										</span>
									</button>
								)
							})}
							{(hiddenRouteIds.size > 0 || depotHidden) && (
								<button
									type="button"
									className="RoutesScreen__multi-select-map-legend-reset"
									onClick={() => {
										setHiddenRouteIds(new Set())
										setDepotHidden(false)
									}}
								>
									{Strings.routes.showAllOnMap}
								</button>
							)}
						</div>
					</div>
				)}
			</div>

			{contractDetails?.allowExternalDelivery && (
				<ExternalDeliveryModal
					visible={showModal}
					partner={selectedPartner}
					submitting={submitting}
					onConfirm={handleSubmit}
					onCancel={() => {
						setShowModal(false)
						setSelectedPartner(null)
					}}
					routeCount={selectedRoutes.length}
					orderCount={totalDeliveriesCount}
					failedRoutes={failedRoutes}
					onCloseFailedRoutes={clearFailedRoutes}
				/>
			)}
		</div>
	)
}

export default MultiRoutePanel
